import { useEffect, useState } from 'react'
import { Check, Loader2, Send, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useAppStore } from '@/stores/appStore'
import { buildAppFeedbackInsert, saveAppFeedback } from './appFeedback'

export function AppFeedbackDrawer({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { usuario } = useAuth()
  const organizationId = useAppStore(state => state.organizacionActivaId)
  const [message, setMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  const trimmed = message.trim()
  const canSubmit = Boolean(usuario?.id) && trimmed.length >= 3 && !saving

  const submit = async () => {
    if (!usuario?.id || !canSubmit) return
    setSaving(true)
    try {
      await saveAppFeedback(buildAppFeedbackInsert({
        userId: usuario.id,
        organizationId,
        message,
        currentPath: window.location.pathname,
        userAgent: navigator.userAgent,
      }))
      setSubmitted(true)
      setMessage('')
      toast.success('Feedback guardado')
    } catch {
      toast.error('No se pudo guardar el feedback')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Cerrar feedback"
        className="absolute inset-0 cursor-default bg-black/35"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-feedback-title"
        className="absolute right-0 top-0 flex h-full w-full max-w-[360px] flex-col border-l border-white/[0.08] bg-[#0d1117] shadow-2xl shadow-black/40"
      >
        <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
          <div>
            <h2 id="app-feedback-title" className="text-[14px] font-semibold text-white/88">
              Retroalimentacion
            </h2>
            <p className="mt-0.5 text-[11.5px] text-white/34">
              Cuéntanos que mejorarías de RadCore.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-md p-1.5 text-white/35 transition-colors hover:bg-white/[0.05] hover:text-white/70"
          >
            <X className="h-4 w-4" strokeWidth={1.7} />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 px-4 py-4">
          {submitted ? (
            <div className="flex items-center gap-2 rounded-md border border-emerald-400/15 bg-emerald-400/[0.06] px-3 py-2 text-[12px] text-emerald-200/78">
              <Check className="h-4 w-4 flex-shrink-0" strokeWidth={1.8} />
              Gracias. Lo tendremos en cuenta.
            </div>
          ) : null}

          <label className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/30" htmlFor="app-feedback-message">
            Comentario
          </label>
          <textarea
            id="app-feedback-message"
            value={message}
            onChange={event => {
              setMessage(event.target.value)
              if (submitted) setSubmitted(false)
            }}
            maxLength={1200}
            placeholder="Escribe una idea, problema o comentario corto..."
            className="min-h-[180px] resize-none rounded-md border border-white/[0.08] bg-white/[0.035] px-3 py-2.5 text-[13px] leading-5 text-white/80 placeholder:text-white/20 outline-none transition-colors focus:border-teal-300/35 focus:bg-white/[0.045]"
          />
          <div className="flex items-center justify-between gap-3 text-[11px] text-white/25">
            <span>{trimmed.length}/1200</span>
            <Button
              type="button"
              tamano="sm"
              onClick={() => void submit()}
              disabled={!canSubmit}
              icono={saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            >
              Enviar
            </Button>
          </div>
        </div>
      </aside>
    </div>
  )
}
